import { useState } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { TID } from '../lib/testIds';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PROJECT_TYPES = [
  { value: 'brand_content', label: 'Brand Content' },
  { value: 'weddings', label: 'Weddings' },
  { value: 'sports', label: 'Sports & Athletes' },
  { value: 'events_music', label: 'Events & Music' },
  { value: 'business', label: 'Business' },
  { value: 'realestate', label: 'Real Estate' },
  { value: 'other', label: 'Something else' },
];

export default function Contact() {
  const [form, setForm] = useState({
    name: '',
    email: '',
    project_type: 'brand_content',
    budget: '',
    message: '',
  });
  const [status, setStatus] = useState('idle'); // idle | submitting | success | error
  const [errorMsg, setErrorMsg] = useState('');

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error('Please fill in your name, email and message.');
      return;
    }
    setStatus('submitting');
    setErrorMsg('');
    try {
      await axios.post(`${API}/contact`, form);
      setStatus('success');
      toast.success("Inquiry received — we'll be in touch within 48 hours.");
      setForm({ name: '', email: '', project_type: 'brand_content', budget: '', message: '' });
    } catch (err) {
      console.error(err);
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : 'Something went wrong. Try again or email hello@utmedia.com.au directly.';
      setErrorMsg(msg);
      setStatus('error');
      toast.error(msg);
    }
  };

  return (
    <section
      id="contact"
      data-testid={TID.contactRoot}
      className="relative bg-transparent border-t border-white/10 overflow-hidden"
    >
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-0">
        {/* Left intro */}
        <div className="lg:col-span-5 px-6 md:px-14 pt-24 md:pt-32 pb-12 lg:pb-32 lg:border-r border-white/10 flex flex-col justify-between">
          <div>
            <p className="text-[10px] md:text-xs uppercase tracking-[0.3em] text-[#D8FF3A] neon-text-glow mb-8 inline-flex items-center gap-3">
              ◆ Start a project
              <span className="inline-block h-px w-10 bg-[#D8FF3A]/50" />
            </p>
            <motion.h2
              initial={{ y: 40, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              viewport={{ once: true, amount: 0.4 }}
              transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
              className="font-display font-bold text-white tracking-tighter leading-[1.02] md:leading-[0.9] text-balance"
              style={{ fontSize: 'clamp(34px, 7vw, 112px)' }}
            >
              Let's make<br />
              <span className="text-white/40">something</span><br />
              worth keeping.
            </motion.h2>
          </div>

          <div className="mt-14 space-y-8">
            <div>
              <p className="text-[10px] uppercase tracking-[0.3em] text-white/40 mb-2">Direct</p>
              <a
                href="mailto:hello@utmedia.com.au"
                className="font-display text-lg md:text-2xl text-white border-b border-white/30 hover:border-white transition-colors"
              >
                hello@utmedia.com.au
              </a>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-[0.3em] text-white/40 mb-2">Studio</p>
              <p className="text-white/70">Newcastle · Sydney</p>
              <p className="text-white/40 text-sm mt-1">By appointment</p>
            </div>
          </div>
        </div>

        {/* Form */}
        <div className="lg:col-span-7 px-6 md:px-14 py-16 md:py-24 lg:py-32">
          <form
            data-testid={TID.contactForm}
            onSubmit={submit}
            className="space-y-12 max-w-2xl"
          >
            <Field label="01 · Your name" required>
              <input
                data-testid={TID.contactName}
                type="text"
                value={form.name}
                onChange={update('name')}
                placeholder="Jane Doe"
                className="w-full bg-transparent border-b border-white/20 pb-4 text-white text-lg md:text-xl placeholder:text-white/30 focus:border-white focus:outline-none transition-colors"
                required
              />
            </Field>

            <Field label="02 · Email" required>
              <input
                data-testid={TID.contactEmail}
                type="email"
                value={form.email}
                onChange={update('email')}
                placeholder="you@studio.com"
                className="w-full bg-transparent border-b border-white/20 pb-4 text-white text-lg md:text-xl placeholder:text-white/30 focus:border-white focus:outline-none transition-colors"
                required
              />
            </Field>

            <Field label="03 · Project type">
              <div className="flex flex-wrap gap-2">
                {PROJECT_TYPES.map((t) => (
                  <button
                    type="button"
                    key={t.value}
                    data-testid={`${TID.contactProjectType}-${t.value}`}
                    onClick={() => setForm({ ...form, project_type: t.value })}
                    className={`px-4 py-2.5 text-xs uppercase tracking-[0.2em] border transition-all ${
                      form.project_type === t.value
                        ? 'bg-[#D8FF3A] text-black border-[#D8FF3A] neon-glow'
                        : 'bg-transparent text-white/70 border-white/20 hover:border-[#D8FF3A]/60 hover:text-white'
                    }`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            </Field>

            <Field label="04 · Budget (optional)">
              <input
                data-testid={TID.contactBudget}
                type="text"
                value={form.budget}
                onChange={update('budget')}
                placeholder="$5,000 – $10,000 AUD"
                className="w-full bg-transparent border-b border-white/20 pb-4 text-white text-lg md:text-xl placeholder:text-white/30 focus:border-white focus:outline-none transition-colors"
              />
            </Field>

            <Field label="05 · Tell us about the project" required>
              <textarea
                data-testid={TID.contactMessage}
                value={form.message}
                onChange={update('message')}
                rows={5}
                placeholder="Vision, timing, references, location…"
                className="w-full bg-transparent border-b border-white/20 pb-4 text-white text-lg md:text-xl placeholder:text-white/30 focus:border-white focus:outline-none transition-colors resize-none"
                required
              />
            </Field>

            <div className="flex flex-wrap items-center justify-between gap-6 pt-4">
              <button
                type="submit"
                data-testid={TID.contactSubmit}
                disabled={status === 'submitting'}
                className="group inline-flex items-center gap-4 bg-[#D8FF3A] text-black px-10 md:px-14 py-5 md:py-6 text-xs md:text-sm font-bold uppercase tracking-[0.25em] hover:neon-glow hover:bg-white transition-all disabled:opacity-60 disabled:cursor-wait"
              >
                {status === 'submitting' ? 'Sending…' : 'Send inquiry'}
                <span
                  aria-hidden
                  className="transition-transform duration-500 group-hover:translate-x-1"
                >
                  →
                </span>
              </button>
              <p className="text-xs text-white/40 max-w-xs">
                We reply within 48 hours, weekdays. By submitting, you agree to be contacted about
                your project.
              </p>
            </div>

            {status === 'success' && (
              <motion.div
                data-testid={TID.contactSuccess}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="border border-white/15 bg-white/[0.03] p-6"
              >
                <p className="text-[10px] uppercase tracking-[0.3em] text-white/50 mb-2">
                  ✓ Inquiry received
                </p>
                <p className="text-white/80">
                  Thanks — we'll be in touch within 48 hours at the email you provided.
                </p>
              </motion.div>
            )}
            {status === 'error' && (
              <p data-testid={TID.contactError} className="text-sm text-red-300">
                {errorMsg}
              </p>
            )}
          </form>
        </div>
      </div>
    </section>
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      <label className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-white/50 mb-4">
        <span>{label}</span>
        {required && <span className="text-white/30">·</span>}
        {required && <span className="text-white/30">required</span>}
      </label>
      {children}
    </div>
  );
}
